import { Head, Link, router } from "@inertiajs/react"
import { Dices, Search, X } from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"

import { DecadeBadge } from "@/components/directory/decade-badge"
import { SiteCard } from "@/components/directory/site-card"
import { Spinner } from "@/components/ui/spinner"
import { withPublicLayout } from "@/layouts/public-layout"
import { cn } from "@/lib/utils"
import { randomPath, rootPath, submitPath } from "@/routes"
import type {
  DirectoryFilters,
  DirectoryStats,
  Pagination,
  SiteCard as SiteCardData,
  SortOption,
  StatusFilter,
} from "@/types/site"

interface Props {
  sites: SiteCardData[]
  pagination: Pagination
  filters: DirectoryFilters
  stats: DirectoryStats
}

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "down", label: "Down" },
  { value: "archived", label: "Archived" },
]

const SORTS: { value: SortOption; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "az", label: "A–Z" },
  { value: "shuffle", label: "Shuffle" },
]

function visit(filters: DirectoryFilters) {
  const params: Record<string, string | number> = {}
  if (filters.q) params.q = filters.q
  if (filters.status !== "all") params.status = filters.status
  if (filters.decade) params.decade = filters.decade
  if (filters.sort !== "newest") params.sort = filters.sort
  if (filters.sort === "shuffle" && filters.seed) params.seed = filters.seed

  router.get(rootPath(), params, {
    only: ["sites", "pagination", "filters"],
    reset: ["sites"],
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

export default function SitesIndex({
  sites,
  pagination,
  filters,
  stats,
}: Props) {
  const [query, setQuery] = useState(filters.q)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinel = useRef<HTMLDivElement>(null)

  // Debounced search.
  useEffect(() => {
    if (query === filters.q) return
    const timer = setTimeout(() => visit({ ...filters, q: query }), 300)
    return () => clearTimeout(timer)
  }, [query, filters])

  // Infinite scroll: fetch the next page when the sentinel comes into view.
  useEffect(() => {
    const node = sentinel.current
    if (!node || !pagination.has_more) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadingMore) return
        setLoadingMore(true)
        router.reload({
          only: ["sites", "pagination"],
          data: { page: pagination.page + 1 },
          preserveUrl: true,
          onFinish: () => setLoadingMore(false),
        })
      },
      { rootMargin: "800px 0px" },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [pagination.page, pagination.has_more, loadingMore])

  const filtered =
    filters.q !== "" || filters.status !== "all" || filters.decade !== null

  return (
    <>
      <Head title="Single Serving Sites Directory" />

      <section className="mx-auto max-w-[1400px] px-4 pt-14 pb-8 sm:px-6 sm:pt-20">
        <p className="text-brand mb-4 font-mono text-xs font-medium tracking-widest uppercase">
          One site. One purpose.
        </p>
        <h1 className="max-w-4xl text-5xl leading-[1.02] font-extrabold tracking-tight text-balance sm:text-7xl">
          The internet&apos;s single-serving websites, all in one place.
        </h1>
        <p className="text-muted-foreground mt-6 max-w-2xl text-lg text-pretty">
          Domains that do exactly one thing, from a single joke to a single
          tool. A headless browser screenshots every one of them, and an uptime
          check visits each site hourly, so you know which still work.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-sm">
          <Stat value={stats.total} label="sites" />
          <Stat value={stats.live} label="live right now" dot="bg-status-up" />
          <Stat
            value={stats.archived}
            label="gone but archived"
            dot="bg-status-archived"
          />
        </div>
      </section>

      <section className="bg-background/90 z-30 border-y backdrop-blur-md lg:sticky lg:top-16">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center">
          <label className="relative flex-1 lg:max-w-sm">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search titles, domains, authors"
              className="bg-card focus:border-brand focus:ring-brand/20 h-10 w-full rounded-xl border pr-3 pl-9 text-sm focus:ring-4 focus:outline-none"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <Segmented>
              {STATUS_TABS.map((tab) => (
                <SegmentButton
                  key={tab.value}
                  active={filters.status === tab.value}
                  onClick={() => visit({ ...filters, status: tab.value })}
                >
                  {tab.label}
                </SegmentButton>
              ))}
            </Segmented>

            <div className="flex items-center gap-1.5">
              {[1990, 2000, 2010, 2020].map((decade) => (
                <button
                  key={decade}
                  type="button"
                  onClick={() =>
                    visit({
                      ...filters,
                      decade: filters.decade === decade ? null : decade,
                    })
                  }
                  className={cn(
                    "rounded-md p-1 transition-opacity",
                    filters.decade && filters.decade !== decade
                      ? "opacity-35 hover:opacity-80"
                      : "opacity-100",
                  )}
                  aria-pressed={filters.decade === decade}
                  title={`${stats.decades[decade] ?? 0} sites`}
                >
                  <DecadeBadge decade={decade} />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 lg:ml-auto">
            <span className="text-muted-foreground font-mono text-xs">
              {pagination.total} {pagination.total === 1 ? "site" : "sites"}
            </span>
            {filtered && (
              <button
                type="button"
                onClick={() => {
                  setQuery("")
                  visit({
                    q: "",
                    status: "all",
                    decade: null,
                    sort: filters.sort,
                    seed: filters.seed,
                  })
                }}
                className="text-brand bg-brand-soft inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
              >
                <X className="size-3" /> Clear
              </button>
            )}
            <select
              value={filters.sort}
              onChange={(event) => {
                const sort = event.target.value as SortOption
                visit({
                  ...filters,
                  sort,
                  seed:
                    sort === "shuffle" ? Math.floor(Math.random() * 1e6) : null,
                })
              }}
              className="bg-card h-9 rounded-lg border py-0 pr-8 pl-3 text-sm"
              aria-label="Sort"
            >
              {SORTS.map((sort) => (
                <option key={sort.value} value={sort.value}>
                  {sort.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6">
        {sites.length === 0 ? (
          <div className="text-muted-foreground rounded-2xl border border-dashed py-24 text-center">
            <p className="text-foreground text-lg font-semibold">
              Nothing matches that.
            </p>
            <p className="mt-1">
              Know one we&apos;re missing?{" "}
              <Link href={submitPath()} className="text-brand underline">
                Submit it
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
            {sites.map((site, index) => (
              <SiteCard key={site.slug} site={site} eager={index < 8} />
            ))}
          </div>
        )}

        <div ref={sentinel} className="flex h-24 items-center justify-center">
          {loadingMore && <Spinner />}
        </div>

        <div className="bg-muted/60 mt-8 flex flex-col items-center gap-4 rounded-3xl border px-6 py-12 text-center">
          <p className="text-2xl font-bold">Feeling adventurous?</p>
          <p className="text-muted-foreground">
            Skip the browsing. We&apos;ll send you straight to a random live
            site.
          </p>
          <a
            href={randomPath()}
            className="bg-brand hover:bg-brand-hover inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white transition-transform hover:scale-105 active:scale-95"
          >
            <Dices className="size-5" /> Take me somewhere
          </a>
        </div>
      </section>
    </>
  )
}

function Stat({
  value,
  label,
  dot,
}: {
  value: number
  label: string
  dot?: string
}) {
  return (
    <span className="inline-flex items-center gap-2">
      {dot && <span className={cn("size-2 rounded-full", dot)} />}
      <span className="text-foreground font-semibold">
        {value.toLocaleString()}
      </span>
      <span className="text-muted-foreground">{label}</span>
    </span>
  )
}

function Segmented({ children }: { children: ReactNode }) {
  return <div className="bg-muted inline-flex rounded-lg p-1">{children}</div>
}

function SegmentButton({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-md px-3 py-1 text-sm font-medium transition-colors",
        active
          ? "bg-card text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  )
}

SitesIndex.layout = withPublicLayout
